import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
  FlatList,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from '../components/DirectionalIcon';
import { ScreenLayout, Header, ConfirmationModal } from '../components';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { useThemedStyles } from '../hooks/useThemedStyles';

export const QuickReplyItemsListScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  console.log('🎯 [QuickReplyItemsListScreen] Component rendered');
  const { navigateTo, showNotification, activeTab, setActiveTab } = useAppContext();
  const [loading, setLoading] = useState(true);
  const [quickReplySets, setQuickReplySets] = useState<any[]>([]);
  const [itemsMap, setItemsMap] = useState<Map<string, {
    item: any;
    sets: Array<{ _id: string; name: string }>;
  }>>(new Map());
  const [items, setItems] = useState<Array<{
    item: any;
    sets: Array<{ _id: string; name: string }>;
  }>>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingItem, setDeletingItem] = useState<{ itemId: string; setId: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [availableRepliesSets, setAvailableRepliesSets] = useState<Array<{ _id: string; name: string }>>([]);

  useEffect(() => {
    loadAllItems();
  }, []);

  const loadAllItems = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load standalone items directly
      const itemsResponse = await apiService.getQuickReplyItems({
        limit: 1000, // Get all items
        skip: 0,
      });

      // Also load sets to get set information for each item
      const setsResponse = await apiService.getQuickReplySets({
        limit: 1000,
        skip: 0,
      });

      if (itemsResponse.statusCode === 1000 && itemsResponse.data?.items) {
        const allItems = itemsResponse.data.items;
        const sets = setsResponse.statusCode === 1000 && setsResponse.data?.quickReplySets 
          ? setsResponse.data.quickReplySets 
          : [];
        
        setQuickReplySets(sets);
        setAvailableRepliesSets(sets.map((set: any) => ({ _id: set._id, name: set.name })));

        // Process items - setCount is now provided by the backend API
        const itemsArray = allItems.map((item: any) => ({
          item,
          sets: [], // Sets array kept for compatibility, but count comes from item.setCount
        }));

        setItems(itemsArray);
        
        // Build items map for compatibility
        const itemsMap = new Map<string, {
          item: any;
          sets: Array<{ _id: string; name: string }>;
        }>();
        itemsArray.forEach(itemEntry => {
          itemsMap.set(itemEntry.item._id?.toString(), itemEntry);
        });
        setItemsMap(itemsMap);
      } else {
        const errorMessage = itemsResponse.errors?.[0]?.message || 'Failed to load items';
        showNotification('error', 'Error', errorMessage);
      }
    } catch (error: any) {
      console.error('Error loading items:', error);
      showNotification('error', 'Error', error.message || 'Failed to load items');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);


  const handleEditItem = (itemEntry: { item: any; sets: Array<{ _id: string; name: string }> }) => {
    // Navigate to edit screen instead of opening modal
    navigateTo('quick-reply-item-edit', itemEntry.item._id);
  };


  const handleDeleteItem = (itemEntry: { item: any; sets: Array<{ _id: string; name: string }> }) => {
    // Delete standalone item
    setDeletingItem({ itemId: itemEntry.item._id, setId: '' });
    setShowDeleteModal(true);
  };

  const confirmDeleteItem = async () => {
    if (!deletingItem) return;

    try {
      setDeleting(true);
      // Delete standalone item (no longer requires setId)
      const response = await apiService.deleteQuickReplyItem(deletingItem.itemId);

      if (response.statusCode === 1000) {
        showNotification('success', 'Success', 'Item deleted successfully.');
        setShowDeleteModal(false);
        setDeletingItem(null);
        await loadAllItems();
      } else {
        const errorMessage = response.errors?.[0]?.message || 'Failed to delete item';
        showNotification('error', 'Error', errorMessage);
      }
    } catch (error: any) {
      console.error('Error deleting item:', error);
      showNotification('error', 'Error', error.message || 'Failed to delete item');
    } finally {
      setDeleting(false);
    }
  };

  const handleBack = () => {
    if (Platform.OS === 'web') {
      window.history.back();
    } else {
      navigateTo('quick-replies');
    }
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const getItemTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      fixed_text: 'Fixed Text',
      data_set: 'Nested Set',
      product: 'Product',
      catalog: 'Catalog',
      webhook: 'Webhook',
      url: 'URL',
      task: 'Task',
      calendar: 'Calendar',
      assign_user: 'Assign User',
      assign_team: 'Assign Team',
    };
    return labels[type] || type;
  };

  const renderItem = ({ item: itemEntry }: { item: { item: any; sets: Array<{ _id: string; name: string }> } }) => {
    const { item, sets } = itemEntry;
    
    return (
      <TouchableOpacity
        style={styles.itemCard}
        onPress={() => handleEditItem(itemEntry)}
        activeOpacity={0.7}
      >
        <View style={styles.itemContent}>
          <View style={styles.itemHeader}>
            <View style={styles.itemInfo}>
              <View style={styles.itemTitleRow}>
                {item.icon && <Text style={styles.itemIcon}>{item.icon}</Text>}
                <Text style={styles.itemDisplayText}>{item.displayText}</Text>
                {!item.isActive && (
                  <View style={styles.inactiveBadge}>
                    <Text style={styles.inactiveBadgeText}>Inactive</Text>
                  </View>
                )}
              </View>
              <Text style={styles.itemType}>{getItemTypeLabel(item.type)}</Text>
              <View style={styles.setsContainer}>
                <Ionicons name="layers-outline" size={14} color={colors.textLight} />
                <Text style={styles.setsText}>
                  In {item.setCount || 0} {(item.setCount || 0) === 1 ? 'set' : 'sets'}
                </Text>
              </View>
            </View>
            <View style={styles.itemActions}>
              <TouchableOpacity
                style={styles.actionButton}
                onPress={(e) => {
                  e.stopPropagation();
                  handleEditItem(itemEntry);
                }}
                activeOpacity={0.7}
              >
                <Ionicons name="create-outline" size={18} color={colors.primary} />
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.actionButton}
                onPress={(e) => {
                  e.stopPropagation();
                  handleDeleteItem(itemEntry);
                }}
                activeOpacity={0.7}
              >
                <Ionicons name="trash-outline" size={18} color={colors.error} />
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  if (loading) {
    return (
      <ScreenLayout
        header={
          <Header
            showNotifications={true}
            showMessages={true}
            onNotificationPress={() => navigateTo('notifications')}
            onMessagePress={() => navigateTo('chat')}
            showNavigation={Platform.OS === 'web'}
            activeTab={activeTab}
            onTabPress={handleTabPress}
          />
        }
        showBottomTabs={Platform.OS !== 'web'}
        onLeftPaneClose={() => {}}
        leftPaneContent="settings"
        onSettingsItemPress={async (itemId) => {
          const { setPendingSettingsItem } = await import('../utils/tempStorage');
          await setPendingSettingsItem(itemId);
          navigateTo('settings');
        }}
        onRightPaneClose={() => {}}
        rightPaneContent={null}
      >
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.loadingText}>Loading items...</Text>
        </View>
      </ScreenLayout>
    );
  }

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      rightPaneContent={null}
    >
      <View style={styles.container}>
        <View style={styles.header}>
          <View style={styles.headerRow}>
            <TouchableOpacity
              style={styles.backButton}
              onPress={handleBack}
            >
              <DirectionalIcon name="arrow-back" size={24} color={colors.text.primary} />
            </TouchableOpacity>
            <View style={styles.titleContainer}>
              <Text style={styles.title}>Reply Items</Text>
              <Text style={styles.subtitle}>
                {items.length} {items.length === 1 ? 'item' : 'items'} across all sets
              </Text>
            </View>
            <TouchableOpacity
              style={styles.flowsButton}
              onPress={() => navigateTo('flows-list')}
              activeOpacity={0.8}
            >
              <Ionicons name="git-branch-outline" size={18} color={colors.primary} />
              <Text style={styles.flowsButtonText}>Flows</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.addButton}
              onPress={() => navigateTo('quick-reply-item-create')}
              activeOpacity={0.8}
            >
              <Ionicons name="add" size={24} color={colors.white} />
            </TouchableOpacity>
          </View>
        </View>

        {items.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Ionicons name="list-outline" size={64} color={colors.textLight} />
            <Text style={styles.emptyText}>No items yet</Text>
            <Text style={styles.emptySubtext}>
              Items are created within sets. Go to a set to add items.
            </Text>
            <TouchableOpacity
              style={styles.emptyButton}
              onPress={() => navigateTo('quick-replies')}
              activeOpacity={0.7}
            >
              <Ionicons name="grid-outline" size={20} color={colors.primary} />
              <Text style={styles.emptyButtonText}>Go to Sets</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <FlatList
            data={items}
            renderItem={renderItem}
            keyExtractor={(itemEntry, index) => itemEntry.item._id || `item-${index}`}
            contentContainerStyle={styles.listContent}
            showsVerticalScrollIndicator={false}
          />
        )}

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          visible={showDeleteModal}
          title="Delete Item"
          message="Are you sure you want to delete this item? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={confirmDeleteItem}
          onCancel={() => {
            setShowDeleteModal(false);
            setDeletingItem(null);
          }}
          confirmButtonColor={colors.error}
          loading={deleting}
        />
      </View>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.lightGray,
  },
  header: {
    padding: spacing.lg,
    backgroundColor: colors.background.surface,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    padding: spacing.xs,
    marginRight: spacing.md,
  },
  titleContainer: {
    flex: 1,
  },
  title: {
    fontSize: typography.fontSize.xl,
    fontFamily: typography.fontFamily.bold,
    color: colors.textDark,
    marginBottom: spacing.xs,
  },
  subtitle: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
  },
  flowsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs / 2,
    paddingHorizontal: spacing.md,
    height: 40,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.primary,
    backgroundColor: `${String(colors.primary)}10`,
    marginRight: spacing.sm,
  },
  flowsButtonText: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
    fontFamily: typography.fontFamily.semibold,
  },
  addButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    ...designTokens.shadows.sm,
  },
  listContent: {
    padding: spacing.md,
    paddingBottom: spacing.xl,
  },
  itemCard: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    padding: spacing.md,
    marginBottom: spacing.md,
    ...designTokens.shadows.sm,
  },
  itemContent: {
    flex: 1,
  },
  itemHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  itemInfo: {
    flex: 1,
    marginRight: spacing.md,
  },
  itemTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.xs / 2,
  },
  itemIcon: {
    fontSize: 20,
  },
  itemDisplayText: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
    flex: 1,
  },
  inactiveBadge: {
    backgroundColor: (colors.warning as string) + '20',
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
    borderRadius: designTokens.borderRadius.sm,
  },
  inactiveBadgeText: {
    fontSize: typography.fontSize.xs,
    color: colors.warning,
    fontFamily: typography.fontFamily.medium,
  },
  itemType: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
    marginBottom: spacing.xs,
  },
  setsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs / 2,
    marginTop: spacing.xs,
  },
  setsText: {
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
    flex: 1,
  },
  itemActions: {
    flexDirection: 'row',
    gap: spacing.xs,
  },
  actionButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: colors.lightGray,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl * 2,
  },
  emptyText: {
    fontSize: typography.fontSize.xl,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
    marginTop: spacing.lg,
    marginBottom: spacing.xs,
  },
  emptySubtext: {
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
    textAlign: 'center',
    marginBottom: spacing.xl,
  },
  emptyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.primary + '15',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: designTokens.borderRadius.md,
    gap: spacing.xs,
  },
  emptyButtonText: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.primary,
    fontFamily: typography.fontFamily.semibold,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
});

